import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import CandidateRegistration from './CandidateRegistration';
import BatchCreation from './BatchCreation';
import BatchEnrollment from './BatchEnrollment';
import BulkAssessments from './BulkAssessments';
import BulkCertificates from './BulkCertificates';
import './Dashboard.css';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';

const AssessmentForm = ({ open, onClose, batchId, candidateData = [] }) => {
  const [formData, setFormData] = useState({
    batchId: batchId || '',
    candidates: candidateData.map(candidate => ({
      candidateID: candidate.candidateId || candidate.candidateID || '',
      trainingDetails: {
        trainingStatus: candidate.trainingStatus || 'Completed',
        attendance: parseFloat(candidate.attendance) || 0
      },
      assessmentDetails: {
        assessmentStatus: candidate.assessmentStatus || 'Pass',
        assessmentPercentage: parseFloat(candidate.assessmentPercentage) || 0,
        grade: candidate.grade || 'A',
        assessmentDataUploadedOn: new Date().toISOString(),
        assessmentAgency: candidate.assessmentAgency || 'Self',
        assessorID: candidate.assessorID || '',
        assessorName: candidate.assessorName || ''
      },
      certificationDetails: {
        certificationName: candidate.certificationName || '',
        isCertified: candidate.isCertified === 'true' || candidate.isCertified === true,
        certifyingAgency: candidate.certifyingAgency || '',
        certificationDate: candidate.certificationDate || new Date().toISOString()
      }
    }))
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (candidateIndex, field, value) => {
    const updatedCandidates = [...formData.candidates];
    const keys = field.split('.');
    
    if (keys.length === 1) {
      updatedCandidates[candidateIndex][keys[0]] = value;
    } else if (keys.length === 2) {
      updatedCandidates[candidateIndex][keys[0]][keys[1]] = value;
    } else if (keys.length === 3) {
      updatedCandidates[candidateIndex][keys[0]][keys[1]][keys[2]] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      candidates: updatedCandidates
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await fetch('https://h7456dbk-8000.inc1.devtunnels.ms/api/assessment/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to submit assessments');
      }

      onClose(true);
    } catch (err) {
      setError(err.message);
      console.error('Error submitting assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
        <DialogTitle>Submit Assessments</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Candidate ID</TableCell>
                  <TableCell>Training Status</TableCell>
                  <TableCell>Assessment Status</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.candidates.map((candidate, index) => (
                  <TableRow key={candidate.candidateID}>
                    <TableCell>{candidate.candidateID}</TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={candidate.trainingDetails.trainingStatus}
                          onChange={(e) => handleInputChange(index, 'trainingDetails.trainingStatus', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="Completed">Completed</MenuItem>
                          <MenuItem value="In Progress">In Progress</MenuItem>
                          <MenuItem value="Not Started">Not Started</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={candidate.assessmentDetails.assessmentStatus}
                          onChange={(e) => handleInputChange(index, 'assessmentDetails.assessmentStatus', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="Pass">Pass</MenuItem>
                          <MenuItem value="Fail">Fail</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Grade</InputLabel>
                        <Select
                          value={candidate.assessmentDetails.grade}
                          onChange={(e) => handleInputChange(index, 'assessmentDetails.grade', e.target.value)}
                          label="Grade"
                        >
                          <MenuItem value="A">A</MenuItem>
                          <MenuItem value="B">B</MenuItem>
                          <MenuItem value="C">C</MenuItem>
                          <MenuItem value="D">D</MenuItem>
                          <MenuItem value="F">F</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={candidate.assessmentDetails.assessmentPercentage}
                        onChange={(e) => handleInputChange(index, 'assessmentDetails.assessmentPercentage', e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [protectedData, setProtectedData] = useState(null);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState({
    loading: false,
    success: false,
    error: null,
    results: []
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, logout } = useAuth();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadStatus({
      loading: true,
      success: false,
      error: null,
      results: []
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://h7456dbk-8000.inc1.devtunnels.ms/api/assessment/upload-csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to process CSV file');
      }

      setUploadStatus({
        loading: false,
        success: true,
        error: null,
        results: result.results || []
      });
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setUploadStatus({
        loading: false,
        success: false,
        error: err.message || 'Error processing CSV file',
        results: []
      });
    }
  };

  const resetUploadStatus = () => {
    setUploadStatus({
      loading: false,
      success: false,
      error: null,
      results: []
    });
  };

  const BatchDetailsPage = ({ batchId }) => {
    const [showAssessmentForm, setShowAssessmentForm] = useState(false);
    const [candidateIds, setCandidateIds] = useState(['CAN_3368433']); // Replace with actual candidate IDs
  
    const handleAssessmentComplete = (success) => {
      setShowAssessmentForm(false);
      if (success) {
        // Refresh the batch data or show success message
        console.log('Assessment submitted successfully');
      }
    };
  
    return (
      <div>
        {/* Your existing batch details content */}
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setShowAssessmentForm(true)}
        >
          Submit Assessments
        </Button>
  
        <AssessmentForm
          open={showAssessmentForm}
          onClose={handleAssessmentComplete}
          batchId={batchId}
          candidateIds={candidateIds}
        />
      </div>
    );
  };
  
  // Set active tab based on URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/candidates')) return 'candidates';
    if (path.includes('/batches')) return 'batches';
    if (path.includes('/batch-enrollment')) return 'batch-enrollment';
    if (path.includes('/assessments')) return 'assessments';
    if (path.includes('/certificates')) return 'certificates';
    return 'candidates'; // Default to candidates instead of overview
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTab());
  
  // Update active tab when URL changes
  useEffect(() => {
    const tab = getActiveTab();
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.pathname]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Update URL without page reload
    navigate(`/dashboard/${tab}`);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Training Partner Dashboard</h1>
        <div className="dashboard-nav">
          <button 
            onClick={() => handleTabChange('candidates')}
            className={`nav-button ${activeTab === 'candidates' ? 'active' : ''}`}
          >
            👥 Candidate Registration
          </button>
          <button 
            onClick={() => handleTabChange('batches')}
            className={`nav-button ${activeTab === 'batches' ? 'active' : ''}`}
          >
            🗂️ Batch Management
          </button>
          <button 
            onClick={() => handleTabChange('batch-enrollment')}
            className={`nav-button ${activeTab === 'batch-enrollment' ? 'active' : ''}`}
          >
            📋 Bulk Enrollment
          </button>
          <button 
            onClick={() => handleTabChange('assessments')}
            className={`nav-button ${activeTab === 'assessments' ? 'active' : ''}`}
          >
            📊 Bulk Assessment
          </button>
          <button 
            onClick={() => handleTabChange('certificates')}
            className={`nav-button ${activeTab === 'certificates' ? 'active' : ''}`}
          >
            🏆 Certificate Management
          </button>
        </div>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </div>

      {activeTab === 'candidates' && (
        <div className="dashboard-content">
          <CandidateRegistration />
        </div>
      )}
      
      {activeTab === 'batches' && (
        <div className="dashboard-content">
          <BatchCreation />
        </div>
      )}
      
      {activeTab === 'batch-enrollment' && (
        <div className="dashboard-content">
          <BatchEnrollment />
        </div>
      )}
      {activeTab === 'assessments' && (
        <div className="dashboard-content">
          <BulkAssessments />
        </div>
      )}
      {activeTab === 'certificates' && (
        <div className="dashboard-content">
          <BulkCertificates />
        </div>
      )}

  {/* Duplicate certificate generation/download cards and results removed as per user request */}
    </div>
  );

  // Add the certificate operation handler
  const handleCertificateOperation = async (event, operation) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('operation', operation);

    setUploadStatus({
      loading: true,
      success: false,
      error: null,
      results: []
    });

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = operation === 'generate' 
        ? '/api/certificates/bulk-generate' 
        : operation === 'download' 
        ? '/api/certificates/bulk-download'
        : '/api/certificates/bulk-status';
        
      const response = await fetch(`https://sims.ethnotech.in${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || `Failed to ${operation} certificates`);
      }

      setUploadStatus({
        loading: false,
        success: true,
        error: null,
        results: result.results || []
      });
    } catch (err) {
      console.error(`Error with certificate ${operation}:`, err);
      setUploadStatus({
        loading: false,
        success: false,
        error: err.message || `Error with certificate ${operation}`,
        results: []
      });
    }

    // Reset file input
    event.target.value = '';
  };
};

export default Dashboard;
